import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
    Calendar,
    Clock,
    Users,
    UserCheck,
    TrendingUp,
    FileText,
    Settings,
    Plus,
    Download,
    Filter,
    Search,
    Eye,
    Edit,
    CheckCircle,
    XCircle,
    AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import StaffAttendanceManager from '../components/attendance/StaffAttendanceManager';
import TeacherAttendanceManager from '../components/attendance/TeacherAttendanceManager';
import AdminAttendanceTaker from '../components/attendance/AdminAttendanceTaker';
import EnhancedAttendanceTaker from '../components/attendance/EnhancedAttendanceTaker';
import BulkAttendanceManager from '../components/attendance/BulkAttendanceManager';
import CalendarEditor from '../components/attendance/CalendarEditor';
import RFIDAttendanceListener from '../components/attendance/RFIDAttendanceListener';
import { AttendanceDashboard as AttendanceDashboardType, AttendanceRecord, LeaveRequest } from '../types/attendance';

const AttendanceDashboard: React.FC = () => {
    const { user } = useAuth();
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();

    const [dashboardData, setDashboardData] = useState<AttendanceDashboardType>({
        todayAttendance: {
            totalEmployees: 0,
            present: 0,
            absent: 0,
            late: 0,
            onLeave: 0
        },
        monthlyStats: {
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            totalWorkingDays: 0,
            averageAttendance: 0,
            totalOvertime: 0
        },
        recentActivities: {
            checkIns: 0,
            checkOuts: 0,
            leaveRequests: 0,
            approvals: 0
        }
    });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            // TODO: Implement API calls to fetch dashboard data
            // For now, using mock data
            setDashboardData({
                todayAttendance: {
                    totalEmployees: 45,
                    present: 38,
                    absent: 4,
                    late: 2,
                    onLeave: 1
                },
                monthlyStats: {
                    month: new Date().getMonth() + 1,
                    year: new Date().getFullYear(),
                    totalWorkingDays: 22,
                    averageAttendance: 85.5,
                    totalOvertime: 45.5
                },
                recentActivities: {
                    checkIns: 12,
                    checkOuts: 8,
                    leaveRequests: 3,
                    approvals: 2
                }
            });
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            showError('Error', 'Failed to fetch attendance data');
        } finally {
            setLoading(false);
        }
    };

    const getAttendancePercentage = () => {
        const { totalEmployees, present } = dashboardData.todayAttendance;
        return totalEmployees > 0 ? Math.round((present / totalEmployees) * 100) : 0;
    };

    const canAccessAttendance = () => {
        if (!user) return false;
        const allowedRoles = ['admin', 'hr', 'principal'];
        return allowedRoles.includes(user.role);
    };

    if (!canAccessAttendance()) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-gray-600">You don't have permission to access attendance management.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <Calendar className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Attendance Management</h2>
                        <p className="text-gray-600">Monitor and manage staff and teacher attendance</p>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <Button
                        variant="secondary"
                        onClick={() => navigate('/attendance/reports')}
                        className="flex items-center space-x-2"
                    >
                        <FileText className="h-4 w-4" />
                        <span>Reports</span>
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => navigate('/attendance/settings')}
                        className="flex items-center space-x-2"
                    >
                        <Settings className="h-4 w-4" />
                        <span>Settings</span>
                    </Button>
                </div>
            </div>

            {/* RFID Attendance Listener */}
            <RFIDAttendanceListener isActive={true} />

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card glassy={true} className="islamic-pattern">
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Today's Attendance</p>
                                <p className="text-2xl font-bold text-gray-900">{getAttendancePercentage()}%</p>
                                <p className="text-sm text-gray-500">
                                    {dashboardData.todayAttendance.present} of {dashboardData.todayAttendance.totalEmployees} present
                                </p>
                            </div>
                            <div className="p-3 bg-emerald-100 rounded-full">
                                <UserCheck className="h-6 w-6 text-emerald-600" />
                            </div>
                        </div>
                    </div>
                </Card>

                <Card glassy={true} className="islamic-pattern">
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Absent Today</p>
                                <p className="text-2xl font-bold text-red-600">{dashboardData.todayAttendance.absent}</p>
                                <p className="text-sm text-gray-500">Employees absent</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-full">
                                <XCircle className="h-6 w-6 text-red-600" />
                            </div>
                        </div>
                    </div>
                </Card>

                <Card glassy={true} className="islamic-pattern">
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Late Arrivals</p>
                                <p className="text-2xl font-bold text-yellow-600">{dashboardData.todayAttendance.late}</p>
                                <p className="text-sm text-gray-500">Employees late</p>
                            </div>
                            <div className="p-3 bg-yellow-100 rounded-full">
                                <Clock className="h-6 w-6 text-yellow-600" />
                            </div>
                        </div>
                    </div>
                </Card>

                <Card glassy={true} className="islamic-pattern">
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">On Leave</p>
                                <p className="text-2xl font-bold text-blue-600">{dashboardData.todayAttendance.onLeave}</p>
                                <p className="text-sm text-gray-500">Employees on leave</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full">
                                <Calendar className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Monthly Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card glassy={true} className="islamic-pattern">
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Overview</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Working Days</span>
                                <span className="font-medium">{dashboardData.monthlyStats.totalWorkingDays}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Average Attendance</span>
                                <span className="font-medium">{dashboardData.monthlyStats.averageAttendance}%</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Total Overtime</span>
                                <span className="font-medium">{dashboardData.monthlyStats.totalOvertime} hrs</span>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card glassy={true} className="islamic-pattern">
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Check-ins Today</span>
                                <span className="font-medium">{dashboardData.recentActivities.checkIns}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Check-outs Today</span>
                                <span className="font-medium">{dashboardData.recentActivities.checkOuts}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Pending Leave Requests</span>
                                <span className="font-medium">{dashboardData.recentActivities.leaveRequests}</span>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Quick Actions */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                        <Button
                            onClick={() => navigate('/attendance/take')}
                            className="flex items-center space-x-2 justify-center"
                        >
                            <Calendar className="h-4 w-4" />
                            <span>Take Attendance</span>
                        </Button>
                        <Button
                            onClick={() => navigate('/attendance/bulk')}
                            className="flex items-center space-x-2 justify-center"
                        >
                            <Users className="h-4 w-4" />
                            <span>Bulk Attendance</span>
                        </Button>
                        <Button
                            onClick={() => navigate('/attendance/calendar-editor')}
                            className="flex items-center space-x-2 justify-center"
                        >
                            <Edit className="h-4 w-4" />
                            <span>Calendar Editor</span>
                        </Button>
                        <Button
                            onClick={() => navigate('/attendance/staff')}
                            className="flex items-center space-x-2 justify-center"
                        >
                            <Users className="h-4 w-4" />
                            <span>Staff Attendance</span>
                        </Button>
                        <Button
                            onClick={() => navigate('/attendance/teachers')}
                            className="flex items-center space-x-2 justify-center"
                        >
                            <UserCheck className="h-4 w-4" />
                            <span>Teacher Attendance</span>
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => navigate('/attendance/leave-requests')}
                            className="flex items-center space-x-2 justify-center"
                        >
                            <FileText className="h-4 w-4" />
                            <span>Leave Requests</span>
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => navigate('/attendance/reports')}
                            className="flex items-center space-x-2 justify-center"
                        >
                            <Download className="h-4 w-4" />
                            <span>Generate Reports</span>
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const StaffAttendance: React.FC = () => {
    const navigate = useNavigate();

    return (
        <StaffAttendanceManager onBack={() => navigate('/attendance')} />
    );
};

const TeacherAttendance: React.FC = () => {
    const navigate = useNavigate();

    return (
        <TeacherAttendanceManager onBack={() => navigate('/attendance')} />
    );
};

const TakeAttendance: React.FC = () => {
    const navigate = useNavigate();

    return (
        <EnhancedAttendanceTaker onBack={() => navigate('/attendance')} />
    );
};

const BulkAttendance: React.FC = () => {
    const navigate = useNavigate();

    return (
        <BulkAttendanceManager onBack={() => navigate('/attendance')} />
    );
};

const CalendarEditorPage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <CalendarEditor onBack={() => navigate('/attendance')} />
    );
};

const LeaveRequests: React.FC = () => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Leave Requests</h2>
                    <p className="text-gray-600">Review and manage leave requests</p>
                </div>
                <Button onClick={() => window.history.back()}>
                    Back
                </Button>
            </div>
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <p className="text-gray-600">Leave request management will be implemented here.</p>
                </div>
            </Card>
        </div>
    );
};

const AttendanceReports: React.FC = () => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Attendance Reports</h2>
                    <p className="text-gray-600">Generate and view attendance reports</p>
                </div>
                <Button onClick={() => window.history.back()}>
                    Back
                </Button>
            </div>
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <p className="text-gray-600">Attendance reports will be implemented here.</p>
                </div>
            </Card>
        </div>
    );
};

const AttendanceSettings: React.FC = () => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Attendance Settings</h2>
                    <p className="text-gray-600">Configure attendance rules and settings</p>
                </div>
                <Button onClick={() => window.history.back()}>
                    Back
                </Button>
            </div>
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <p className="text-gray-600">Attendance settings will be implemented here.</p>
                </div>
            </Card>
        </div>
    );
};

const Attendance: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<AttendanceDashboard />} />
            <Route path="/staff" element={<StaffAttendance />} />
            <Route path="/teachers" element={<TeacherAttendance />} />
            <Route path="/take" element={<TakeAttendance />} />
            <Route path="/bulk" element={<BulkAttendance />} />
            <Route path="/calendar-editor" element={<CalendarEditorPage />} />
            <Route path="/leave-requests" element={<LeaveRequests />} />
            <Route path="/reports" element={<AttendanceReports />} />
            <Route path="/settings" element={<AttendanceSettings />} />
        </Routes>
    );
};

export default Attendance;
